import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { loadOperationsSummary } from './operations-summary-port';
import { loadSiteInspectionSummary } from './site-inspection-summary-port';

export function meetingMonthRange(month: string) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/u.test(month)) throw Error('INVALID_MONTH');
  return {
    from: `${month}-01`,
    to: new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)), 0))
      .toISOString()
      .slice(0, 10),
  };
}
export function previousMeetingMonth(month: string) {
  meetingMonthRange(month);
  return new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)) - 2, 1))
    .toISOString()
    .slice(0, 7);
}
async function settled<T>(read: () => Promise<T>) {
  try {
    return { state: 'ready' as const, value: await read() };
  } catch {
    return { state: 'unavailable' as const, value: null };
  }
}
async function readMonthlyMeeting(uid: string, siteId: string | null, month: string, fresh: boolean) {
  const period = meetingMonthRange(month);
  const [operations, inspection, budget, meetings] = await Promise.all([
    settled(() => {
      if (!siteId) throw Error('SITE_OPERATIONS_SUMMARY_UNAVAILABLE');
      return (
      loadOperationsSummary({
        siteId,
        day: new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }),
        month,
        features: [
          'tbm',
          'regular-risk',
          'safety-training',
          'evacuation-drill',
          'work-permit',
          'inspection-reporting',
        ],
        fresh,
      }));
    }),
    settled(() => {
      if (!siteId) throw Error('SITE_INSPECTION_SUMMARY_UNAVAILABLE');
      return loadSiteInspectionSummary(uid, siteId, period, fresh);
    }),
    settled(async () => {
      const { createBudgetOpexRepository } =
        await import('../budget-materials/budget-opex-repository');
      const port = createBudgetOpexRepository(null, siteId);
      if (!port) throw Error('UNAVAILABLE');
      const page = await port.load({
        from: month,
        to: month,
        owner: 'principal',
        company: '',
        category: '',
        search: '',
        offset: 0,
      });
      if (!page.complete || page.heldCount !== 0) throw Error('BUDGET_SUMMARY_INCOMPLETE');
      return page;
    }),
    settled(async () => {
      const { createMeetingMinutesRepository } =
        await import('../communications/meeting-minutes-repository');
      return createMeetingMinutesRepository(siteId).list({
        search: '',
        dateFrom: period.from,
        dateTo: period.to,
        kind: 'committee',
        cadence: 'monthly',
        cursor: null,
      });
    }),
  ]);
  return { month, operations, inspection, budget, meetings, at: Date.now() };
}
export type MonthlyMeetingRead = Awaited<ReturnType<typeof readMonthlyMeeting>>;
const cache = createSessionKeyedReadCache<MonthlyMeetingRead>(8);
export function peekMonthlyMeeting(uid: string, siteId: string | null, month: string): MonthlyMeetingRead | null {
  if (!uid) return null;
  meetingMonthRange(month);
  return cache.peek(`${uid}:${siteId ?? 'no-site'}:${month}`);
}
export function loadMonthlyMeeting(uid: string, siteId: string | null, month: string, force = false) {
  if (!uid) return Promise.reject(Error('IDENTITY_REQUIRED'));
  meetingMonthRange(month);
  const key = `${uid}:${siteId ?? 'no-site'}:${month}`;
  const hit = cache.peek(key);
  return cache.read(
    key,
    () => readMonthlyMeeting(uid, siteId, month, force),
    force || !hit || Date.now() - hit.at > 60_000,
  );
}
